import { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/tailwind/input";
import { suggestDistrictsForStreet } from "@/services/district-street-services";
import { useAuth } from "@/context/use-auth";
import { DistrictStreet } from "@/models/district";

export interface StreetSuggestion {
    street: string;
    districtId: string;
    districtName: string;
}

interface StreetAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onSuggestionSelect: (suggestion: StreetSuggestion) => void;
    onBlur?: () => void;
    placeholder?: string;
    invalid?: boolean;
    'aria-label'?: string;
    'data-testid'?: string;
    id?: string;
    autoComplete?: string;
}

export function StreetAutocomplete({
    value,
    onChange,
    onSuggestionSelect,
    onBlur,
    placeholder,
    invalid,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
    id,
    autoComplete,
}: StreetAutocompleteProps) {
    const { token } = useAuth();
    const [suggestions, setSuggestions] = useState<DistrictStreet[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const uniqueSuggestions = suggestions.reduce<DistrictStreet[]>((acc, s) => {
        if (!acc.some(a => a.street === s.street && a.districtId === s.districtId)) {
            acc.push(s);
        }
        return acc;
    }, []);

    const fetchSuggestions = useCallback(async (query: string) => {
        if (!token || !query.trim() || query.trim().length < 2) {
            setSuggestions([]);
            setIsOpen(false);
            return;
        }

        try {
            const results = await suggestDistrictsForStreet(token, query.trim());
            if (results && results.length > 0) {
                setSuggestions(results);
                setIsOpen(true);
                setActiveIndex(-1);
            } else {
                setSuggestions([]);
                setIsOpen(false);
            }
        } catch {
            setSuggestions([]);
            setIsOpen(false);
        }
    }, [token]);

    function handleInputChange(newValue: string) {
        onChange(newValue);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        if (!newValue.trim() || newValue.trim().length < 2) {
            setSuggestions([]);
            setIsOpen(false);
            return;
        }

        debounceTimer.current = setTimeout(() => {
            fetchSuggestions(newValue);
        }, 300);
    }

    function handleSelect(suggestion: DistrictStreet) {
        setIsOpen(false);
        setSuggestions([]);
        onSuggestionSelect({
            street: suggestion.street,
            districtId: suggestion.districtId,
            districtName: suggestion.districtName,
        });
    }

    function handleKeyDown(e: React.KeyboardEvent) {
        if (!isOpen || uniqueSuggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex(prev =>
                    prev < uniqueSuggestions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex(prev =>
                    prev > 0 ? prev - 1 : uniqueSuggestions.length - 1
                );
                break;
            case 'Enter':
                if (activeIndex >= 0) {
                    e.preventDefault();
                    handleSelect(uniqueSuggestions[activeIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                break;
        }
    }

    function handleBlur(_e: React.FocusEvent) {
        setTimeout(() => {
            if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
                setIsOpen(false);
                onBlur?.();
            }
        }, 150);
    }

    useEffect(() => {
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    return (
        <div ref={containerRef} className="relative">
            <Input
                id={id}
                type="text"
                value={value}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                placeholder={placeholder}
                data-invalid={invalid || null}
                aria-label={ariaLabel}
                data-testid={dataTestId}
                autoComplete={autoComplete}
                role="combobox"
                aria-expanded={isOpen}
                aria-autocomplete="list"
            />
            {isOpen && uniqueSuggestions.length > 0 && (
                <ul
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 dark:bg-zinc-800 dark:ring-white/10"
                    role="listbox"
                    data-testid="street-suggestions-list"
                >
                    {uniqueSuggestions.map((suggestion, index) => (
                        <li
                            key={`${suggestion.street}-${suggestion.districtId}`}
                            role="option"
                            aria-selected={index === activeIndex}
                            data-testid={`street-suggestion-${index}`}
                            className={`cursor-pointer px-3 py-2 ${
                                index === activeIndex
                                    ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                                    : 'text-zinc-900 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-zinc-700'
                            }`}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSelect(suggestion);
                            }}
                            onMouseEnter={() => setActiveIndex(index)}
                        >
                            <span className="font-medium">{suggestion.street}</span>
                            <span className="ml-2 text-zinc-500 dark:text-zinc-400">
                                &rarr; {suggestion.districtName}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
